import { useState } from 'react';
import {
  downloadText,
  fetchTablePreview,
  runDatabaseExport,
  type CallerIdentity,
  type DbColumn,
  type DbTable,
} from '@/lib/dbAdmin';

interface Props {
  identity: CallerIdentity;
  tables: DbTable[];
}

function cellValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > 60 ? `${s.slice(0, 60)}…` : s;
  }
  return String(v);
}

export default function TableExplorer({ identity, tables }: Props) {
  const [query, setQuery] = useState('');
  const [busyCsv, setBusyCsv] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [previewTable, setPreviewTable] = useState<string | null>(null);
  const [previewCols, setPreviewCols] = useState<DbColumn[]>([]);
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const filtered = tables.filter((t) => {
    const q = query.trim().toLowerCase();
    return !q || t.name.toLowerCase().includes(q);
  });

  const openPreview = async (table: string) => {
    setPreviewTable(table);
    setPreviewCols([]);
    setPreviewRows([]);
    setPreviewError('');
    setPreviewLoading(true);
    try {
      const res = await fetchTablePreview(identity, table, 30);
      setPreviewCols(res.columns);
      setPreviewRows(res.rows);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Erreur de chargement.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const exportOne = async (table: string) => {
    setBusyCsv(table);
    try {
      const res = await runDatabaseExport(identity, 'csv', [table]);
      const file = res.files?.[0];
      if (file) downloadText(file.name, file.content, 'text/csv');
    } catch {
      /* silencieux : l'utilisateur peut réessayer */
    } finally {
      setBusyCsv(null);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-table-line text-lg"></i>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground-950">Tables de la base</h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            {tables.length} tables · consultation et aperçu uniquement (aucune modification possible).
          </p>
        </div>
      </div>

      <div className="relative mb-3">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filtrer les tables…"
          className="w-full h-10 pl-9 pr-4 rounded-md border border-background-200/70 bg-background-100 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
        />
      </div>

      <div className="border border-background-200/70 rounded-lg overflow-hidden divide-y divide-background-200/60 max-h-[560px] overflow-y-auto">
        {filtered.map((t) => {
          const open = expanded === t.name;
          const pkSet = new Set(t.primary_key);
          return (
            <div key={t.name}>
              <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-background-100/60 transition-colors">
                <button
                  onClick={() => setExpanded(open ? null : t.name)}
                  className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer flex-shrink-0"
                  title={open ? 'Masquer les colonnes' : 'Voir la structure des colonnes'}
                >
                  <i className={open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}></i>
                </button>
                <i className="ri-table-2 text-foreground-400"></i>
                <button
                  onClick={() => setExpanded(open ? null : t.name)}
                  className="min-w-0 flex-1 text-left cursor-pointer"
                >
                  <div className="text-sm font-medium text-foreground-900 truncate">{t.name}</div>
                  <div className="text-[11px] text-foreground-400">
                    {t.row_count.toLocaleString('fr-FR')} lignes · {t.columns.length} colonnes
                    {t.primary_key.length > 0 && ` · PK : ${t.primary_key.join(', ')}`}
                  </div>
                </button>
                <button
                  onClick={() => openPreview(t.name)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <i className="ri-eye-line"></i>
                  Aperçu
                </button>
                <button
                  onClick={() => exportOne(t.name)}
                  disabled={busyCsv === t.name}
                  className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer disabled:opacity-50"
                  title="Télécharger cette table en CSV"
                >
                  {busyCsv === t.name ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-excel-2-line"></i>}
                </button>
              </div>

              {open && (
                <div className="px-4 pb-3 pt-1 bg-background-100/40">
                  <div className="border border-background-200/70 rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-background-100/70 text-foreground-500">
                          <th className="text-left px-3 py-1.5 font-medium">Colonne</th>
                          <th className="text-left px-3 py-1.5 font-medium">Type</th>
                          <th className="text-left px-3 py-1.5 font-medium hidden sm:table-cell">Clé</th>
                          <th className="text-left px-3 py-1.5 font-medium hidden md:table-cell">Null</th>
                          <th className="text-left px-3 py-1.5 font-medium hidden md:table-cell">Défaut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {t.columns.map((c) => (
                          <tr key={c.name} className="border-t border-background-200/60">
                            <td className="px-3 py-1.5 text-foreground-900 font-medium whitespace-nowrap">
                              {c.name}
                              {pkSet.has(c.name) && (
                                <i className="ri-key-2-line text-accent-600 ml-1" title="Clé primaire"></i>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-foreground-600">
                              {c.data_type}
                              {c.max_length ? `(${c.max_length})` : ''}
                            </td>
                            <td className="px-3 py-1.5 hidden sm:table-cell">
                              {pkSet.has(c.name) ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-100 text-accent-900 font-medium">PK</span>
                              ) : (
                                <span className="text-foreground-300">—</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 hidden md:table-cell text-foreground-500">
                              {c.is_nullable ? 'oui' : 'non'}
                            </td>
                            <td className="px-3 py-1.5 hidden md:table-cell text-foreground-400">
                              <span className="block max-w-[220px] truncate" title={c.col_default || ''}>
                                {c.col_default || '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-sm text-foreground-400 py-8 text-center">Aucune table correspondante.</p>
        )}
      </div>

      {previewTable && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setPreviewTable(null)}
        >
          <div
            className="bg-background-50 rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-foreground-950 truncate">{previewTable}</h3>
                <p className="text-xs text-foreground-500">Aperçu — 30 premières lignes</p>
              </div>
              <button
                onClick={() => setPreviewTable(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-5 overflow-auto">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
                </div>
              ) : previewError ? (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{previewError}</p>
              ) : previewRows.length === 0 ? (
                <p className="text-sm text-foreground-400 py-8 text-center">Table vide.</p>
              ) : (
                <div className="overflow-x-auto border border-background-200/70 rounded-lg">
                  <table className="text-xs w-full">
                    <thead>
                      <tr className="bg-background-100/70">
                        {previewCols.map((c) => (
                          <th key={c.name} className="text-left px-3 py-2 font-semibold text-foreground-600 whitespace-nowrap border-b border-background-200/70">
                            {c.name}
                            <span className="block text-[10px] font-normal text-foreground-400">{c.data_type}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i} className="border-b border-background-200/50 last:border-0">
                          {previewCols.map((c) => (
                            <td key={c.name} className="px-3 py-2 text-foreground-700 whitespace-nowrap max-w-[240px] truncate">
                              {cellValue(row[c.name])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}